import { AssetSvg } from '@/components/foundation/Svg'
import { classes } from '@lc/libreact'
import {
	type ComponentPropsWithoutRef,
	type MouseEvent,
	type MouseEventHandler,
} from 'react'
import { Link } from 'wouter'
import { ImageAsset } from '../foundation/ImageAsset'
import styles from './Button.scss'
import Spinner from './Spinner'

export type ButtonOnClick =
	| ((e: MouseEvent<HTMLSpanElement>) => Promise<void>)
	| MouseEventHandler<HTMLSpanElement>

type ButtonProps = {
	className?: string
	iconClassName?: string
	label?: string
	iconUrl?: string
	imageUrl?: string
	size?: 'small' | 'medium'
	level?:
		| 'primary'
		| 'secondary'
		| 'close'
		| 'popup'
		| 'map-options-selector'
		| 'map-option'
	onClick?: ButtonOnClick
	rounded?: boolean // L,M,S ??
	border?: boolean
	status?: 'ready' | 'loading' | 'error'
	alignLeft?: boolean
	shadow?: boolean
	disabled?: boolean
	selected?: boolean
	ref?: React.Ref<HTMLButtonElement>
}

export const Button = ({
	className,
	iconClassName,
	label,
	iconUrl,
	imageUrl,
	size,
	level,
	onClick,
	rounded,
	border,
	status,
	alignLeft,
	shadow,
	disabled = false,
	selected = false,
	ref,
	...props
}: Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'type'> &
	ButtonProps) => {
	const renderButtonContent = () => {
		if (status === 'loading') {
			return <Spinner />
		}
		if (!label) return
		return <span className={styles.labelBtn}>{label}</span>
	}

	return (
		<button
			ref={ref}
			type="button"
			className={`standard-shadow ${classes(
				className,
				styles.button,
				[styles.border, border],
				[styles.primary, level === 'primary'],
				[styles.secondary, level === 'secondary'],
				[styles.close, level === 'close'],
				[styles.popup, level === 'popup'],
				[styles.mapOptions, level === 'map-options-selector'],
				[styles.mapOption, level === 'map-option'],
				[styles.small, size === 'small'],
				[styles.medium, size === 'medium'],
				[styles.rounded, rounded],
				[styles.shadow, shadow],
				[styles.selected, selected],
				[styles.alignLeft, alignLeft],
			)}`}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			onClick={onClick as any}
			{...props}
			disabled={disabled}
		>
			{iconUrl && (
				<AssetSvg
					className={classes(iconClassName ?? styles.iconBtn)}
					name={iconUrl}
				/>
			)}
			{imageUrl && (
				<ImageAsset src={imageUrl} className={iconClassName} />
			)}
			{renderButtonContent()}
		</button>
	)
}

export const ButtonLink = ({
	className,
	iconClassName,
	label,
	iconUrl,
	size,
	href,
	level,
	rounded,
	border,
	replace,
	...props
}: ComponentPropsWithoutRef<'a'> &
	Omit<ButtonProps, 'onClick' | 'ref'> & {
		href: string
		replace?: boolean
	}) => {
	return (
		<Link
			href={href}
			replace={replace}
			className={`standard-shadow ${classes(
				styles.button,
				[styles.border, border],
				[styles.primary, level === 'primary'],
				[styles.secondary, level === 'secondary'],
				[styles.small, size === 'small'],
				[styles.medium, size === 'medium'],
				[styles.rounded, rounded],
				className,
			)}`}
			{...props}
		>
			{iconUrl && (
				<AssetSvg
					className={classes(iconClassName ?? styles.iconBtn)}
					name={iconUrl}
				/>
			)}
			{label && <span className={styles.labelBtn}>{label}</span>}
		</Link>
	)
}

export const ExternalLink = ({
	className,
	iconClassName,
	label,
	iconUrl,
	size,
	href,
	level,
	rounded,
	border,
	shadow,
	...props
}: ComponentPropsWithoutRef<'a'> &
	Omit<ButtonProps, 'onClick' | 'ref'> & { href: string }) => {
	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className={classes(
				styles.button,
				[styles.border, border],
				[styles.primary, level === 'primary'],
				[styles.secondary, level === 'secondary'],
				[styles.small, size === 'small'],
				[styles.medium, size === 'medium'],
				[styles.rounded, rounded],
				[styles.shadow, shadow],
				className,
			)}
			{...props}
		>
			{iconUrl && (
				<AssetSvg
					className={classes(iconClassName ?? styles.iconBtn)}
					name={iconUrl}
				/>
			)}
			{label && <span className={styles.labelBtn}>{label}</span>}
		</a>
	)
}
